import {
  type ShippingFeeSettings,
  shippingFeeSettingsSchema,
  type UpdateShippingFeeSettingsInput,
} from '@repo/api-contracts';

export interface ShippingFeeFormValues {
  readonly baseFee: string;
  readonly jejuExtraFee: string;
  readonly remoteExtraFee: string;
}
/** 칸별 오류 + `total`(기본 + 지역 추가 합계가 상한을 넘을 때의 폼 전체 오류). */
export type ShippingFeeFormErrors = Partial<Record<keyof ShippingFeeFormValues | 'total', string>>;
export type ShippingFeeFormResult =
  | { readonly ok: true; readonly input: UpdateShippingFeeSettingsInput }
  | { readonly ok: false; readonly errors: ShippingFeeFormErrors };

const RANGE_MESSAGE = '0 ~ 1,000,000 사이의 원 단위 정수로 입력해 주세요.';
/** 숫자만 — 쉼표·소수점·부호·지수 표기는 받지 않는다. */
const INTEGER_PATTERN = /^\d+$/;
export const SHIPPING_FEE_FIELDS = ['baseFee', 'jejuExtraFee', 'remoteExtraFee'] as const;

/** 설정 금액을 입력 칸 문자열로. */
export const shippingFeeFormFrom = (settings: ShippingFeeSettings): ShippingFeeFormValues => ({
  baseFee: String(settings.baseFee),
  jejuExtraFee: String(settings.jejuExtraFee),
  remoteExtraFee: String(settings.remoteExtraFee),
});

const amountOf = (text: string): number =>
  INTEGER_PATTERN.test(text.trim()) ? Number(text.trim()) : Number.NaN;

/**
 * 각 칸 0 ~ SHIPPING_FEE_MAX 정수, 기본 + 가장 큰 지역 추가 ≤ SHIPPING_FEE_MAX — 계약 `shippingFeeSettingsSchema` 로 검사한다
 * (서버와 같은 기준). 합계 오류는 칸이 모두 맞을 때만 `total` 로 붙는다.
 */
export const parseShippingFeeForm = (values: ShippingFeeFormValues): ShippingFeeFormResult => {
  const candidate = {
    baseFee: amountOf(values.baseFee),
    jejuExtraFee: amountOf(values.jejuExtraFee),
    remoteExtraFee: amountOf(values.remoteExtraFee),
  };
  const parsed = shippingFeeSettingsSchema.safeParse(candidate);
  if (parsed.success) return { ok: true, input: parsed.data };
  const errors: ShippingFeeFormErrors = {};
  for (const field of SHIPPING_FEE_FIELDS) {
    if (parsed.error.issues.some((issue) => issue.path[0] === field)) errors[field] = RANGE_MESSAGE;
  }
  const total = parsed.error.issues.find((issue) => issue.path.length === 0);
  if (total && Object.keys(errors).length === 0) errors.total = total.message;
  return { ok: false, errors };
};
